/**
 * What the minted token is called in Settings > Token management.
 *
 * The list shows names, not secrets, so the name is the only thing a user has
 * to decide by when revoking one. It says the product and the device, and it is
 * clamped and stripped of anything that would let a crafted client forge a
 * convincing row next to the user's own tokens.
 */
export const Z3_TOKEN_NAME_PREFIX = 'Zerops Code';

export const ZBOX_TOKEN_NAME_PREFIX = 'zbox CLI';

const MAX_DEVICE = 40;

const SEPARATOR = '\u00b7';

/**
 * Control characters and the separator are dropped: a name is one label, never
 * a second line and never a forged prefix. Done by code point rather than a
 * character-class regex, which the lint rule rightly refuses.
 */
const stripUnsafe = (value: string): string => Array.from(value)
  .map((char) => {
    const code = char.codePointAt(0) ?? 0;
    const isControl = code < 0x20 || code === 0x7f;
    return isControl || char === SEPARATOR ? ' ' : char;
  })
  .join('');

export const buildHandoverTokenName = (prefix: string, device: string | undefined): string => {
  const cleaned = stripUnsafe(device ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_DEVICE)
    .trim();

  return cleaned ? `${prefix} ${SEPARATOR} ${cleaned}` : prefix;
};

export const buildZ3TokenName = (device: string | undefined): string =>
  buildHandoverTokenName(Z3_TOKEN_NAME_PREFIX, device);

export const buildZboxTokenName = (device: string | undefined): string =>
  buildHandoverTokenName(ZBOX_TOKEN_NAME_PREFIX, device);

/**
 * A short label for this browser, read from the user agent rather than taken
 * from the request: the client never gets to choose how its row reads in the
 * user's token list. `buildZ3TokenName` still sanitizes, as defence in depth.
 */
export const describeThisDevice = (userAgent: string): string => {
  const os = /Mac OS X/.test(userAgent) ? 'macOS'
    : /Windows/.test(userAgent) ? 'Windows'
    : /Android/.test(userAgent) ? 'Android'
    : /(iPhone|iPad)/.test(userAgent) ? 'iOS'
    : /Linux/.test(userAgent) ? 'Linux'
    : '';

  const browser = /Edg\//.test(userAgent) ? 'Edge'
    : /OPR\//.test(userAgent) ? 'Opera'
    : /Firefox\//.test(userAgent) ? 'Firefox'
    : /Chrome\//.test(userAgent) ? 'Chrome'
    : /Safari\//.test(userAgent) ? 'Safari'
    : '';

  return [ os, browser ].filter(Boolean).join(' ');
};
